import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import ResultsChart from './ResultChart'
import { Checkbox, Dropdown, Icon, Label } from 'semantic-ui-react'
import { StyledFiltersOptions, StyledFilterValues } from './style'
import { useAuth } from '~/routes/components/AuthProvider'
import { ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG, FARMER_ROLE_SLUG } from '~/Constants'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { El } from '~/components/Layout/style'
import { ThemeContext } from 'styled-components'
import { Nullable } from '~/types'
import Filters from './Filters'
import CO2Footprint from './CO2Fooprint'
import ExcelExport from '~/components/ExcelExport'

export enum Co2FiltersTypes {
  PERIOD = 'periodFrom',
  DATA_TYPES = 'type',
  FARMER_GROUP = 'farmerGroupId',
  FARMER = 'farmerId',
  DATA_COLLECTIONS = 'co2FootprintId',
  RACE = 'breedId',
  INDIVIDUAL_FILTERS = 'customFilterId'

}

const getCo2Filters = () => [
  {
    id: Co2FiltersTypes.PERIOD,
    placeholder: 'Period',
    displayForRole: [ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG, FARMER_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.DATA_TYPES,
    placeholder: 'Types',
    displayForRole: [ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG, FARMER_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.FARMER_GROUP,
    placeholder: 'Search farmer group',
    displayForRole: [ADMIN_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.FARMER,
    placeholder: 'Search farmers',
    displayForRole: [ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.DATA_COLLECTIONS,
    placeholder: 'Data collection',
    displayForRole: [ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.RACE,
    placeholder: 'Race',
    displayForRole: [ADMIN_ROLE_SLUG, FARMER_MANAGER_ROLE_SLUG, FARMER_ROLE_SLUG]
  },
  {
    id: Co2FiltersTypes.INDIVIDUAL_FILTERS,
    placeholder: 'Individual filter',
    displayForRole: [ADMIN_ROLE_SLUG]
  }
]

const defaultDataFilters = {
  resultType: '',
  periodFrom: '',
  periodTo: '',
  co2FootprintId: '',
  farmerId: '',
  customFilterId: '',
  farmerGroupId: '',
  breedId: ''
}

export type DataFilteredType = Nullable<{
  resultType: string
  periodFrom: string
  periodTo: string
  co2FootprintId: string
  farmerId: string
  customFilterId: string
  farmerGroupId: string
  breedId: string
}>

const ClimaCheck: React.FC = () => {
  const { firstColor } = useContext(ThemeContext)
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const [co2Filters, setCo2Filters] = useState(getCo2Filters().filter(filter => filter.displayForRole.find((role) => role === roleSlug)))
  const [checkedFilters, setCheckedFilters] = useState(co2Filters.filter((_, index) => index <= 1))

  const farmerLogged = roleSlug === FARMER_ROLE_SLUG
  const adminLogged = roleSlug === ADMIN_ROLE_SLUG

  const [dataFiltered, setDataFiltered] = useState<DataFilteredType>(null)

  const {
    data: co2Settings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings
  } = ReportingQuery.getClimaReportSettings()

  const {
    data: co2Data,
    isLoading: isLoadingData,
    isFetching: isFetchingCo2Data,
    refetch: fetchCo2Data
  } = ReportingQuery.getClimaCheckReportData(dataFiltered)

  const {
    data: farmersList,
    isLoading: isLoadingFarmList
  } = CO2ClimateQuery.getCo2FarmersList(farmerLogged && dataFiltered?.periodFrom ? { page: 0, search: '' } : null)

  useEffect(() => {
    if (co2Settings) {
      setDataFiltered({
        resultType: 'total',
        periodFrom: co2Settings.periods?.[0]?.periodFrom || '',
        periodTo: co2Settings.periods?.[0]?.periodTo || '',
        co2FootprintId: '',
        farmerId: '',
        customFilterId: '',
        farmerGroupId: '',
        breedId: ''
      })
    }
  }, [co2Settings])

  useEffect(() => {
    if (dataFiltered) {
      fetchCo2Data()
    }
  }, [dataFiltered])

  useEffect(() => {
    if (farmersList && farmersList?.length > 1) {
      const filters = getCo2Filters()
      const subFarmersFilters = []
      if (!co2Filters.find(filter => filter.id === Co2FiltersTypes.RACE)) {
        const breedFilter = filters.find(filter => filter.id === Co2FiltersTypes.RACE)
        subFarmersFilters.push(breedFilter)
      }
      if (!co2Filters.find(filter => filter.id === Co2FiltersTypes.FARMER)) {
        const farmerFilter = filters.find(filter => filter.id === Co2FiltersTypes.FARMER)
        subFarmersFilters.push(farmerFilter)
        setCheckedFilters([...checkedFilters, farmerFilter])
      }
      setCo2Filters([...co2Filters, ...subFarmersFilters])
    }
  }, [farmersList])

  useEffect(() => {
    refetchSettings()
  }, [t, roleSlug])

  useEffect(() => {
    const filters = localStorage.getItem('climaReportingFilters')
    if (filters) {
      const filtersParsed = JSON.parse(filters).filter(filter => filter.displayForRole.find(role => role === roleSlug))
      filtersParsed.forEach(filter => {
        const indexFilter = co2Filters.findIndex(farmerFilter => farmerFilter.id === filter.id)
        if (indexFilter >= 0) {
          filter.placeholder = co2Filters[indexFilter].placeholder
        }
      })
      setCheckedFilters(filtersParsed)
    }
  }, [])

  function renderChart() {
    return <ResultsChart
      isLoading={isLoading || isFetchingCo2Data}
      data={co2Data}
      averageTHGLabel={co2Settings?.texts[0]?.translation}
      barColors={{
        resultsColor: co2Settings.climateCheckFootprintSettings.yourValueColor,
        dairyResultsColor: co2Settings.climateCheckFootprintSettings.dairyValueColor,
        defaultColor: co2Settings.climateCheckFootprintSettings.lflValueColor || '#8884d8'
      }}
      displayDecimal={dataFiltered?.resultType === 'ghg_emission_milk_idf'}
      subSupplierSelected={farmerLogged && Boolean(dataFiltered?.farmerId)}
      resultsUnit={co2Settings?.resultTypes?.find(result => result.key === dataFiltered?.resultType)?.unit || ''}
    />
  }

  function renderFilters() {
    return <Filters
      checkedFilters={checkedFilters}
      dataFiltered={dataFiltered}
      isFetchingCo2Data={isFetchingCo2Data}
      co2Settings={co2Settings}
      handleChangeData={(data) => setDataFiltered(data)}
    />
  }

  function renderActions() {
    return <>
      <El.PageHeaderActionButton
        size='large'
        disabled={checkedFilters.length === 0}
        onClick={() => setDataFiltered({
          ...defaultDataFilters,
          periodFrom: dataFiltered.periodFrom,
          periodTo: dataFiltered.periodTo,
          resultType: dataFiltered.resultType
        })}
      >
        {t('Reset')}
      </El.PageHeaderActionButton>
      <StyledFiltersOptions
        text={t('Filters')}
        icon='filter'
        floating
        labeled
        button
        closeOnChange={false}
        multiple
        className='icon'
        style={{ background: firstColor, color: 'white' }}
      >
        <Dropdown.Menu>
          <Dropdown.Menu scrolling>
            {co2Filters.map((filter, indexFilter) => {
              return indexFilter > 1 && <Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                key={filter.id}
              >
                <Checkbox
                  onClick={(e, data) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (data.checked) {
                      const newArray = [...checkedFilters];
                      newArray.splice(indexFilter, 0, filter);
                      setCheckedFilters(newArray)
                      localStorage.setItem('climaReportingFilters', JSON.stringify([...checkedFilters, filter]))
                    } else {
                      setDataFiltered({
                        ...dataFiltered,
                        [filter.id]: ''
                      })
                      setCheckedFilters(checkedFilters.filter(filterChecked => filterChecked.id !== filter.id))
                      localStorage.setItem('climaReportingFilters', JSON.stringify(checkedFilters.filter(filterChecked => filterChecked.id !== filter.id)))
                    }
                  }}
                  checked={Boolean(checkedFilters.find(filterChecked => filterChecked.id === filter.id))}
                  label={t(filter.placeholder)}
                />
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown.Menu>
      </StyledFiltersOptions>
      <ExcelExport
        url={'reporting/co2-footprint/export'}
        fileName={'Export'}
        downloadWithFileName
        data={{ ...dataFiltered }}
        buttonType={'icon'}
        icon={'file excel outline'}
        name={t('Export')}
      />
    </>
  }

  function getSelectValue(filter) {
    const element = document.getElementById(filter.id)
    const childElement = element?.querySelector('.divider.text')
    return childElement?.innerHTML
  }

  function renderFilterValues() {
    return checkedFilters.map((filter, index) => {
      const filterInputKey = filter.id === Co2FiltersTypes.DATA_TYPES ? 'resultType' : filter.id
      const value = getSelectValue(filter)
      return Boolean(dataFiltered[filterInputKey]) && <Label key={filter.id} style={{ background: firstColor, color: 'white' }} as='a'>
        {`${value}`}
        {index > 1 && <Icon name='delete' onClick={() => {
          setDataFiltered({
            ...dataFiltered,
            [filterInputKey]: ''
          })
        }} />}
      </Label>
    })
  }

  const isLoading = isLoadingData || isLoadingSettings || isLoadingFarmList

  return <Layout
    title={t('Data collections')}
    segmented
    actions={renderActions()}
    filters={!isLoadingFarmList && renderFilters()}
    isBusy={isLoading}
    actionAlignToTop
    actionNoWrap
    isCo2Loader
  >
    <StyledFilterValues>
      {!isLoading && dataFiltered && renderFilterValues()}
    </StyledFilterValues>
    {!isLoading && renderChart()}
    {adminLogged && <CO2Footprint
      dataFiltered={dataFiltered}
    />}
  </Layout>
}
export default ClimaCheck